import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import introJs from 'intro.js';
import { useAuth } from '../../lib/auth';
import { updateOnboarding } from '../../lib/apiClient';

const TOUR_VERSION = 1;

type TooltipPosition =
  | 'floating'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-right-aligned'
  | 'top-left-aligned'
  | 'top-middle-aligned'
  | 'bottom-right-aligned'
  | 'bottom-left-aligned'
  | 'bottom-middle-aligned';

type OnboardingState = {
  completed: boolean;
  step: number;
  version: number;
  lastInspectionId?: string;
};

type StepConfig = {
  route?: string | ((state: OnboardingState) => string | null);
  search?: string;
  steps: Array<{ element: string; intro: string; position?: TooltipPosition }>;
};

const defaultState: OnboardingState = {
  completed: false,
  step: 0,
  version: TOUR_VERSION,
};

const waitForElements = (selectors: string[], timeoutMs = 8000): Promise<boolean> =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const missing = selectors.filter((selector) => !document.querySelector(selector));
      if (missing.length === 0) {
        resolve(true);
        return;
      }
      if (Date.now() - start > timeoutMs) {
        resolve(false);
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });

export function OnboardingTour(): JSX.Element | null {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState>(defaultState);
  const introRef = useRef<ReturnType<typeof introJs> | null>(null);
  const lastStartedStepRef = useRef<number | null>(null);
  const exitReasonRef = useRef<'route-change' | 'completed' | null>(null);

  useEffect(() => {
    if (!user) return;
    const incoming = user.onboarding;
    const nextState: OnboardingState = {
      completed: incoming?.completed ?? false,
      step: typeof incoming?.step === 'number' ? incoming.step : 0,
      version: typeof incoming?.version === 'number' ? incoming.version : TOUR_VERSION,
      lastInspectionId: incoming?.lastInspectionId,
    };
    if (nextState.version !== TOUR_VERSION) {
      nextState.step = 0;
      nextState.completed = false;
      nextState.version = TOUR_VERSION;
    }
    setState(nextState);
  }, [user]);

  const stepConfig = useMemo<StepConfig | null>(() => {
    if (state.completed) return null;
    if (state.step === 0) {
      return {
        route: '/app/report-presets',
        steps: [
          {
            element: '[data-onboarding="template-create"]',
            intro: 'Start by creating your inspection report template. Upload a sample report or add schema fields.',
            position: 'right',
            tooltipClass: 'surface-panel',
            highlightClass: 'surface-panel',

          },
        ],
      };
    }
    if (state.step === 1) {
      return {
        route: '/app/properties',
        steps: [
          {
            element: '[data-onboarding="property-create"]',
            intro: 'Create a property so inspections have an address and owner context.',
            position: 'bottom',
            tooltipClass: 'surface-panel',

          },
        ],
      };
    }
    if (state.step === 2) {
      return {
        route: '/app/items',
        search: 'action=new-inspection',
        steps: [
          {
            element: '[data-onboarding="inspection-preset"]',
            intro: 'Start a new inspection and choose your template in the report preset field.',
            position: 'bottom',
            tooltipClass: 'surface-panel',

          },
        ],
      };
    }
    if (state.step === 3) {
      return {
        route: (current) =>
          current.lastInspectionId ? `/app/items/${current.lastInspectionId}` : null,
        steps: [
          {
            element: '[data-onboarding="photo-upload"]',
            intro: 'Upload inspection photos here to trigger AI room classification and issue detection.',
            position: 'right',
            tooltipClass: 'surface-panel',

          },
          {
            element: '[data-onboarding="report-generate"]',
            intro: 'Generate your report once photos are analyzed. You can download or regenerate anytime.',
            position: 'bottom',
            tooltipClass: 'surface-panel',

          },
        ],
      };
    }
    return null;
  }, [state]);

  const persistState = async (next: Partial<OnboardingState>) => {
    const merged = { ...state, ...next, version: TOUR_VERSION };
    setState(merged);
    try {
      await updateOnboarding({
        completed: merged.completed,
        step: merged.step,
        version: merged.version,
        lastInspectionId: merged.lastInspectionId,
      });
    } catch {
      // Ignore persistence errors; the tour will retry later.
    }
  };

  useEffect(() => {
    if (!user || !stepConfig || state.completed) return;
    const route = typeof stepConfig.route === 'function' ? stepConfig.route(state) : stepConfig.route;
    if (!route) return;

    const expectedPath = route;
    const expectedSearch = stepConfig.search ? `?${stepConfig.search}` : '';
    const hasPath = location.pathname === expectedPath;
    const hasSearch = expectedSearch ? location.search === expectedSearch : true;

    if (!hasPath || !hasSearch) {
      exitReasonRef.current = 'route-change';
      introRef.current?.exit();
      navigate(`${expectedPath}${expectedSearch}`, { replace: true });
      return;
    }

    if (lastStartedStepRef.current === state.step) {
      return;
    }

    const selectors = stepConfig.steps.map((step) => step.element);
    waitForElements(selectors).then((ready) => {
      if (!ready) return;

      const intro = introJs();
      introRef.current = intro;
      lastStartedStepRef.current = state.step;

      intro.setOptions({
        steps: stepConfig.steps,
        showProgress: true,
        showBullets: false,
        exitOnOverlayClick: false,
        disableInteraction: false,
        nextLabel: 'Next',
        prevLabel: 'Back',
        doneLabel: state.step === 3 ? 'Finish' : 'Next',
        // skipLabel: 'Skip',
      });

      intro.oncomplete(async () => {
        exitReasonRef.current = 'completed';
        if (state.step === 0) {
          await persistState({ step: 1, completed: false });
          return;
        }
        if (state.step === 1) {
          await persistState({ step: 2, completed: false });
          return;
        }
        if (state.step === 3) {
          await persistState({ completed: true, step: 4 });
        }
      });

      intro.onexit(async () => {
        if (exitReasonRef.current) {
          exitReasonRef.current = null;
          return;
        }
        await persistState({ completed: true, step: state.step + 1 });
      });

      intro.start();
    });
  }, [location.pathname, location.search, navigate, state, stepConfig, user]);

  return null;
}
